import { CHARACTER_CATALOG, IMAGE_VERSION_MAP, R2_BASE, SHARED_IMG } from '../content/characterCatalog'

// ============================================
// Leka — Character Database (Single Source of Truth)
// Public roster: Kivo / Teli / Movo / Aven / Mone (Nilo parked from public GTM)
// Internal benchmark: Fini
// Frozen: Rela / Zelo / Lumi
// ============================================

export interface Character {
  id: string
  displayName: string
  name: string
  emoji: string
  tier: 1 | 2 | 3
  ethnicity: string
  gender: 'male' | 'female' | 'unknown'
  personality: string
  voice: string
  social: string
  color: string
  bgGradient: string
  imgPosition?: string
  telegramHandle?: string
  available: boolean
  location?: string
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
  bio?: string
  role?: string
  subtitle?: string
  fixedLanguage?: string
  imageOverride?: string
}

export type CharacterCatalogEntry = Character & { ogImage?: string }

export const R2_CDN = `${R2_BASE}/chars`
export const CHARACTERS: Record<string, CharacterCatalogEntry> = CHARACTER_CATALOG
const PUBLIC_CHARACTER_ORDER = ['kivo', 'teli', 'movo', 'nilo', 'aven', 'mone']

export function getCharacter(id: string): CharacterCatalogEntry | undefined {
  return CHARACTERS[id]
}

export function getAllCharacters(): Character[] {
  return Object.values(CHARACTERS)
}

export function getPublicCharacters(): Character[] {
  return getAllCharacters()
    .filter((character) => {
      if (!character.available) return false
      const rootId = character.id.replace(/-jp$/, '')
      return PUBLIC_CHARACTER_ORDER.includes(rootId)
    })
    .sort((a, b) => {
      const aRoot = a.id.replace(/-jp$/, '')
      const bRoot = b.id.replace(/-jp$/, '')
      const aIndex = PUBLIC_CHARACTER_ORDER.indexOf(aRoot)
      const bIndex = PUBLIC_CHARACTER_ORDER.indexOf(bRoot)
      return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
    })
}

export function getCharacterImageUrl(characterOrId: Character | string): string {
  const character = typeof characterOrId === 'string'
    ? getCharacter(characterOrId)
    : characterOrId

  if (!character) return SHARED_IMG
  if (character.imageOverride) return character.imageOverride

  const baseId = character.id.replace(/-jp$/, '')
  const version = IMAGE_VERSION_MAP[baseId as keyof typeof IMAGE_VERSION_MAP] ?? 'v=1'
  return `${R2_CDN}/${baseId}.webp?${version}`
}

export function getCharacterImageSrcSet(characterOrId: Character | string): string {
  const character = typeof characterOrId === 'string'
    ? getCharacter(characterOrId)
    : characterOrId

  if (!character) return `${SHARED_IMG} 1x, ${SHARED_IMG} 2x`
  if (character.imageOverride) return `${character.imageOverride} 1x, ${character.imageOverride} 2x`

  const baseId = character.id.replace(/-jp$/, '')
  const version = IMAGE_VERSION_MAP[baseId as keyof typeof IMAGE_VERSION_MAP] ?? 'v=1'
  return `${R2_CDN}/${baseId}.webp?${version} 1x, ${R2_CDN}/${baseId}@2x.webp?${version} 2x`
}
